import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Row, Col, Card, Badge, Button, Nav, Tab, Table } from 'react-bootstrap';
import './TestCaseDetail.css';

const TestCaseDetail = () => {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState('overview');

  // Mock data
  const testCase = {
    id: id || 'TC-0145',
    title: 'User Login with Valid Credentials',
    status: 'Ready',
    priority: 'High',
    module: 'Authentication',
    assignedTo: 'Jane Smith',
    createdBy: 'John Doe',
    createdOn: '2025-12-10',
    updatedOn: '2025-12-14',
    description: 'Verify that user can login with valid credentials and access the dashboard successfully.',
    prerequisites: [
      'User account exists in the system',
      'Database is accessible',
      'Application is running'
    ],
    testType: 'Functional',
    executionTime: '~5 minutes',
    tags: ['Login', 'Authentication', 'Critical Path'],
    totalExecutions: 24,
    lastPassRate: 95,
    avgExecutionTime: '4m 32s',
    linkedBugsCount: 2
  };

  const testSteps = [
    { step: 1, action: 'Navigate to login page', expectedResult: 'Login page loads successfully', testData: '-' },
    { step: 2, action: 'Enter username', expectedResult: 'Field accepts input', testData: 'testuser@example.com' },
    { step: 3, action: 'Enter password', expectedResult: 'Field is masked', testData: 'Test@123' },
    { step: 4, action: 'Click Login button', expectedResult: 'Dashboard appears', testData: '-' }
  ];

  const executionHistory = [
    { id: 'EX-0234', date: 'Dec 14', tester: 'Jane Smith', result: 'Pass', duration: '4m 20s' },
    { id: 'EX-0189', date: 'Dec 12', tester: 'Jane Smith', result: 'Pass', duration: '4m 45s' },
    { id: 'EX-0156', date: 'Dec 10', tester: 'Bob Lee', result: 'Fail', duration: '3m 10s' },
    { id: 'EX-0134', date: 'Dec 8', tester: 'Jane Smith', result: 'Pass', duration: '4m 30s' }
  ];

  const mantisBugs = [
    {
      id: '#0012345',
      summary: 'Login timeout after 30 seconds',
      status: 'Resolved',
      severity: 'Major',
      reporter: 'Jane Smith',
      assignedTo: 'Tom Kumar'
    },
    {
      id: '#0012389',
      summary: 'Password validation not working',
      status: 'Closed',
      severity: 'Minor',
      reporter: 'Bob Lee',
      assignedTo: 'Sara Wilson'
    }
  ];

  const attachments = [
    { name: 'test-flow-diagram.png', size: '245 KB', uploadedBy: 'John Doe', uploadedOn: '2025-12-10' },
    { name: 'test-data.xlsx', size: '18 KB', uploadedBy: 'Jane Smith', uploadedOn: '2025-12-12' }
  ];

  const getStatusVariant = (status) => {
    const variants = {
      'Ready': 'success',
      'In Progress': 'warning',
      'Deprecated': 'secondary',
      'Blocked': 'danger'
    };
    return variants[status] || 'secondary';
  };

  const getPriorityVariant = (priority) => {
    const variants = {
      'Critical': 'danger',
      'High': 'warning',
      'Medium': 'info',
      'Low': 'secondary'
    };
    return variants[priority] || 'secondary';
  };

  const getResultVariant = (result) => {
    const variants = {
      'Pass': 'success',
      'Fail': 'danger',
      'Blocked': 'warning'
    };
    return variants[result] || 'secondary';
  };

  return (
    <div className="test-case-detail">
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><Link to="/qc/my-view">QC Management</Link></li>
          <li className="breadcrumb-item"><Link to="/qc/test-cases">Test Cases</Link></li>
          <li className="breadcrumb-item active">{testCase.id}</li>
        </ol>
      </nav>

      {/* Header */}
      <Card className="c-card-1 mb-4">
        <Card.Body className="c-card-body-1">
          <Row>
            <Col lg={8}>
              <div className="mb-2">
                <h4 className="mb-2">
                  <span className="text-muted me-2">{testCase.id}</span>
                  {testCase.title}
                </h4>
                <div className="d-flex gap-2 mb-3">
                  <Badge bg={getStatusVariant(testCase.status)}>{testCase.status}</Badge>
                  <Badge bg={getPriorityVariant(testCase.priority)}>{testCase.priority}</Badge>
                  <Badge bg="light" text="dark">{testCase.module}</Badge>
                </div>
              </div>
            </Col>
            <Col lg={4} className="text-end">
              <Button variant="primary" className="me-2">
                <i className="fas fa-play me-2"></i>Execute
              </Button>
              <Button variant="outline-primary" className="me-2">
                <i className="fas fa-edit me-2"></i>Edit
              </Button>
              <Button variant="outline-secondary">
                <i className="fas fa-clone me-2"></i>Clone
              </Button>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col md={6}>
              <small className="text-muted">
                <strong>Assigned To:</strong> {testCase.assignedTo}
              </small>
            </Col>
            <Col md={6}>
              <small className="text-muted">
                <strong>Created:</strong> {testCase.createdOn} by {testCase.createdBy}
              </small>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Tabs */}
      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
        <Card className="c-card-1">
          <Card.Header className="c-card-header-1 pb-0">
            <Nav variant="tabs">
              <Nav.Item>
                <Nav.Link eventKey="overview">
                  <i className="fas fa-info-circle me-2"></i>Overview
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="steps">
                  <i className="fas fa-list-ol me-2"></i>Test Steps
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="history">
                  <i className="fas fa-history me-2"></i>Execution History
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="mantis">
                  <i className="fas fa-bug me-2"></i>Mantis Bugs ({mantisBugs.length})
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="attachments">
                  <i className="fas fa-paperclip me-2"></i>Attachments ({attachments.length})
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </Card.Header>
          <Card.Body className="c-card-body-1">
            <Tab.Content>
              {/* Overview Tab */}
              <Tab.Pane eventKey="overview">
                <Row>
                  <Col lg={8}>
                    <h6 className="mb-3">Test Information</h6>
                    <div className="info-box mb-4">
                      <p><strong>Description:</strong></p>
                      <p>{testCase.description}</p>
                      
                      <p className="mt-3"><strong>Prerequisites:</strong></p>
                      <ul>
                        {testCase.prerequisites.map((prereq, index) => (
                          <li key={index}>{prereq}</li>
                        ))}
                      </ul>
                      
                      <Row className="mt-3">
                        <Col md={6}>
                          <p><strong>Test Type:</strong> {testCase.testType}</p>
                        </Col>
                        <Col md={6}>
                          <p><strong>Execution Time:</strong> {testCase.executionTime}</p>
                        </Col>
                      </Row>

                      <p><strong>Tags:</strong></p>
                      <div className="d-flex gap-2">
                        {testCase.tags.map((tag, index) => (
                          <Badge key={index} bg="info">{tag}</Badge>
                        ))}
                      </div>
                    </div>
                  </Col>
                  <Col lg={4}>
                    <h6 className="mb-3">Quick Stats</h6>
                    <div className="stats-box">
                      <div className="stat-item">
                        <div className="stat-label">Total Executions</div>
                        <div className="stat-value">{testCase.totalExecutions}</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-label">Last Pass Rate</div>
                        <div className="stat-value text-success">{testCase.lastPassRate}%</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-label">Avg Execution Time</div>
                        <div className="stat-value">{testCase.avgExecutionTime}</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-label">Linked Bugs</div>
                        <div className="stat-value">{testCase.linkedBugsCount}</div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Tab.Pane>

              {/* Test Steps Tab */}
              <Tab.Pane eventKey="steps">
                <h6 className="mb-3">Step-by-Step Test Instructions</h6>
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th style={{ width: '60px' }}>#</th>
                      <th>Action</th>
                      <th>Expected Result</th>
                      <th>Test Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {testSteps.map((step) => (
                      <tr key={step.step}>
                        <td><Badge bg="secondary">{step.step}</Badge></td>
                        <td>{step.action}</td>
                        <td>{step.expectedResult}</td>
                        <td>{step.testData}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <div className="mt-3">
                  <Button variant="outline-primary" size="sm">
                    <i className="fas fa-plus me-2"></i>Add Step
                  </Button>
                  <Button variant="outline-secondary" size="sm" className="ms-2">
                    <i className="fas fa-sort me-2"></i>Reorder Steps
                  </Button>
                </div>
              </Tab.Pane>

              {/* Execution History Tab */}
              <Tab.Pane eventKey="history">
                <h6 className="mb-3">Recent Executions</h6>
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Execution ID</th>
                      <th>Date</th>
                      <th>Tester</th>
                      <th>Result</th>
                      <th>Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {executionHistory.map((execution) => (
                      <tr key={execution.id}>
                        <td>
                          <Link to={`/qc/test-execution/${execution.id}`} className="text-primary">
                            {execution.id}
                          </Link>
                        </td>
                        <td>{execution.date}</td>
                        <td>{execution.tester}</td>
                        <td>
                          <Badge bg={getResultVariant(execution.result)}>
                            {execution.result === 'Pass' ? '✅' : '❌'} {execution.result}
                          </Badge>
                        </td>
                        <td>{execution.duration}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Tab.Pane>

              {/* Mantis Bugs Tab */}
              <Tab.Pane eventKey="mantis">
                <div className="alert alert-info mb-3">
                  <i className="fas fa-info-circle me-2"></i>
                  <strong>Read-Only Data:</strong> Bug information is fetched from Mantis Bug Tracker via API integration. Bugs cannot be created or edited from PMS.
                </div>
                <h6 className="mb-3">Linked Bugs</h6>
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Bug ID</th>
                      <th>Summary</th>
                      <th>Status</th>
                      <th>Severity</th>
                      <th>Reporter</th>
                      <th>Assigned To</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mantisBugs.map((bug) => (
                      <tr key={bug.id}>
                        <td>
                          <a 
                            href={`https://mantis.company.com/view.php?id=${bug.id.replace('#', '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary"
                          >
                            {bug.id}
                            <i className="fas fa-external-link-alt ms-1 small"></i>
                          </a>
                        </td>
                        <td>{bug.summary}</td>
                        <td><Badge bg="success">{bug.status}</Badge></td>
                        <td><Badge bg="warning">{bug.severity}</Badge></td>
                        <td>{bug.reporter}</td>
                        <td>{bug.assignedTo}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <div className="mt-3">
                  <a 
                    href="https://mantis.company.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary btn-sm"
                  >
                    <i className="fas fa-external-link-alt me-2"></i>
                    View in Mantis
                  </a>
                </div>
              </Tab.Pane>

              {/* Attachments Tab */}
              <Tab.Pane eventKey="attachments">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">Attachments</h6>
                  <Button variant="primary" size="sm">
                    <i className="fas fa-upload me-2"></i>Upload File
                  </Button>
                </div>
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>File Name</th>
                      <th>Size</th>
                      <th>Uploaded By</th>
                      <th>Uploaded On</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attachments.map((file, index) => (
                      <tr key={index}>
                        <td>
                          <i className={`fas fa-file${file.name.endsWith('.png') ? '-image' : '-excel'} me-2`}></i>
                          {file.name}
                        </td>
                        <td>{file.size}</td>
                        <td>{file.uploadedBy}</td>
                        <td>{file.uploadedOn}</td>
                        <td>
                          <Button variant="link" size="sm" className="text-primary p-0 me-2">
                            <i className="fas fa-download"></i>
                          </Button>
                          <Button variant="link" size="sm" className="text-danger p-0">
                            <i className="fas fa-trash"></i>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Tab.Pane>
            </Tab.Content>
          </Card.Body>
        </Card>
      </Tab.Container>
    </div>
  );
};

export default TestCaseDetail;